import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { getFeatureFlag } from 'ctm-selectors/selectors/base';
import _ from 'lodash';
import { quoteFormSelector } from 'ctm-selectors';
import { displayBooleanString } from 'ctm-util-functions';
import moment from 'moment';
import { convertStringCase, getLicenceTypeLabel } from 'ctm-components/components/utils';
import { SummaryTable } from '../summary_table';
import { getEmploymentLabel } from '../../products/summary/common/details/regular_driver';

const path = 'regular_driver_gender';

const _SummaryRegularDriver = (props) => {
    const { driverGender, driverDob, driverLicenceType, driverEmployment, licenceAge, comprehensiveInsured, comprehensiveInsuredYears, driverPreviousClaims,
        driverClaimsDetails, driverOwnsAnotherCar, driverOwnsAnotherHome, isUpdated, hasAssumedDriverClaims,
        hasAssumedDriverLicenceType, hasAssumedDriverComprehensiveInsured, hasAssumedDriverComprehensiveInsuredYears, env } = props;

    const ccquEnabled = getFeatureFlag('newRegularDriverClaims');

    let claimValue;

    if (ccquEnabled && driverClaimsDetails && !hasAssumedDriverClaims && driverClaimsDetails.size === 0) {
        claimValue = '0';
    } else if (driverPreviousClaims !== null && !ccquEnabled) {
        claimValue = displayBooleanString(driverPreviousClaims);
    } else if (driverClaimsDetails && hasAssumedDriverClaims && ccquEnabled && !isUpdated) {
        claimValue = 'Required';
    } else if (driverClaimsDetails && driverClaimsDetails.size > 0) {
        claimValue = driverClaimsDetails.size;
    } else {
        claimValue = 'No';
    }

    const generateClaimInfo = (claimsDetails) => {
        const claimLookup = (key, value, key2, value2) => {
            const lookupTable = {
                atFault: value === 'YES' ? 'At fault, ' : 'Not at fault, ',
                within3Years: value2 === 'YES' ? 'under 3 years' : 'between 3 and 5 years'
            };
            const atFault = lookupTable[key];
            const within3Years = lookupTable[key2];
            return atFault + within3Years;
        };

        const claimObjects = JSON.parse(JSON.stringify(claimsDetails)).map((claim) => ({
            title: `Claim ${claim.claimNumber}`,
            value: claimLookup('atFault', claim.atFault, 'within3Years', claim.within3Years)
        }));

        return claimObjects;
    };

    const hasDriverAssumedQuestions = hasAssumedDriverLicenceType || hasAssumedDriverClaims || hasAssumedDriverComprehensiveInsured || hasAssumedDriverComprehensiveInsuredYears;
    const regularDriver = [
        {
            title: 'Gender',
            value: convertStringCase(driverGender.toLowerCase(), 'title')
        },
        {
            title: 'Date of birth',
            value: moment(driverDob).format('DD/MM/YYYY')
        },
        {
            title: 'Employment status',
            value: getEmploymentLabel(driverEmployment)
        },
        {
            title: 'Licence type',
            value: getLicenceTypeLabel(driverLicenceType) ?? 'Required',
            userToVerify: hasAssumedDriverLicenceType && !driverLicenceType
        },
        {
            title: 'Licence age',
            value: licenceAge
        },
        {
            title: 'Past comprehensive car insurance',
            value: displayBooleanString(comprehensiveInsured) ?? 'Required',
            userToVerify: hasAssumedDriverComprehensiveInsured && !comprehensiveInsured
        },
        {
            title: 'Years of comprehensive car insurance',
            value: comprehensiveInsuredYears  ?? 'Required',
            userToVerify: hasAssumedDriverComprehensiveInsuredYears && !comprehensiveInsuredYears
        },
        {
            title: ccquEnabled ? 'Number of claims in last 5 years' : 'Claims in the last 5 years',
            value: claimValue,
            userToVerify: ccquEnabled && hasAssumedDriverClaims
        },
        (ccquEnabled && hasAssumedDriverClaims && !isUpdated ? {
            title: 'Regular driver at fault',
            value: 'Required',
            userToVerify: true
        } : null),
        (ccquEnabled && hasAssumedDriverClaims && !isUpdated ? {
            title: 'Within last 3 years',
            value: 'Required',
            userToVerify: true
        } : null),
        // This will generate a nested array of claim objects, which we will flatten before passing to the summary table
        // we will also remove null values from the array before passing to the summary table
        (ccquEnabled && driverClaimsDetails.size > 0 && !hasAssumedDriverClaims ? generateClaimInfo(driverClaimsDetails) : null),
        {
            title: 'Owns another car',
            value: displayBooleanString(driverOwnsAnotherCar) ?? 'No'
        },
        {
            title: 'Owns a home',
            value: displayBooleanString(driverOwnsAnotherHome) ?? 'No'
        }
    ];

    return (
        <SummaryTable
            path={path}
            isUpdated={isUpdated}
            titleLabel={`Regular Driver ${isUpdated ? '- Updated' : ''}`}
            tableContent={_.flatten(regularDriver.filter((item) => item !== null))}
            verifyBucket={ccquEnabled && hasDriverAssumedQuestions}
        />
    );
};

const mapStateToProps = (state) => {
    return {
        driverGender: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'gender']) || '',
        driverDob: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'dob']),
        driverEmployment: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'employmentStatus']),
        driverLicenceType: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'licenceType']),
        licenceAge: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'licenceAge']),
        comprehensiveInsured: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'comprehensiveInsured']),
        comprehensiveInsuredYears: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'comprehensiveInsuredYears']),
        driverPreviousClaims: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'anyPreviousClaims']) ?? null,
        driverClaimsDetails: quoteFormSelector(state).getIn(['coverDetail', 'driver', 'claimsDetails']),
        driverOwnsAnotherCar: quoteFormSelector(state).getIn(['coverDetail', 'ownsAnotherCar']),
        driverOwnsAnotherHome: quoteFormSelector(state).getIn(['coverDetail', 'ownsHome']),
        isUpdated: state.getIn(['vertical', 'quote', 'form', `summaryPageEdit${path}`]),
        hasAssumedDriverClaims: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverClaims']),
        hasAssumedDriverLicenceType: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverLicenceType']),
        hasAssumedDriverComprehensiveInsured: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsured']),
        hasAssumedDriverComprehensiveInsuredYears: state.getIn(['vertical', 'quote', 'form', 'hasAssumedQuestions', 'hasAssumedDriverComprehensiveInsuredYears']),
        env: state.getIn(['serverConfig', 'env'])
    };
};

_SummaryRegularDriver.propTypes = {
    driverGender: PropTypes.string,
    driverDob: PropTypes.string,
    driverEmployment: PropTypes.string,
    driverLicenceType: PropTypes.string,
    driverAge: PropTypes.string,
    comprehensiveInsured: PropTypes.bool,
    comprehensiveInsuredYears: PropTypes.string,
    driverPreviousClaims: PropTypes.string,
    driverClaimsDetails: PropTypes.array,
    driverOwnsAnotherCar: PropTypes.string,
    driverOwnsAnotherHome: PropTypes.string,
    isUpdated: PropTypes.bool,
    hasAssumedDriverClaims: PropTypes.bool,
    hasAssumedDriverLicenceType: PropTypes.bool,
    hasAssumedDriverComprehensiveInsured: PropTypes.bool,
    hasAssumedDriverComprehensiveInsuredYears: PropTypes.string
};

const SummaryRegularDriver = connect(
    mapStateToProps,
)(_SummaryRegularDriver);

export { _SummaryRegularDriver, SummaryRegularDriver };
